import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Info, Lightbulb, TriangleAlert, ImageOff } from 'lucide-react';
import DocVideo from '../../components/ui/DocVideo';
import { shotUrl } from '../videoUrl';
import { parseDocMarkdown } from './parseDocMarkdown';
import { renderInline, inlineText } from './renderInline';
import { headingIdFor } from '../helpContent';

/**
 * Give every heading block its contract id. Ids travel ONLY in the page's
 * `headings[]` (the landing build strips `{#id}` markers from the markdown),
 * so headings are matched to that list IN ORDER: the next listed heading
 * whose text matches is this one. German pages carry the English ids by
 * position, which is exactly what in-order matching preserves. A heading the
 * list does not name falls back to a slug of its text.
 */
function assignHeadingIds(blocks, page) {
  const listed = page?.headings || [];
  let cursor = 0;
  const visit = (list) => {
    for (const block of list) {
      if (block.type === 'callout') visit(block.blocks);
      if (block.type !== 'heading') continue;
      const raw = block.text.trim();
      const plain = inlineText(raw).trim();
      let found = -1;
      for (let i = cursor; i < listed.length; i += 1) {
        if (listed[i].text === raw || listed[i].text === plain) {
          found = i;
          break;
        }
      }
      if (found >= 0) {
        block.id = listed[found].id;
        cursor = found + 1;
      } else {
        block.id = headingIdFor(null, plain, block.level);
      }
    }
  };
  visit(blocks);
  return blocks;
}

const CALLOUT_ICON = { note: Info, tip: Lightbulb, warning: TriangleAlert };

/** A `shot:` screenshot; a missing file shows a neutral plate, never a broken image. */
function Shot({ id, caption }) {
  const [failed, setFailed] = useState(false);
  return failed ? (
    <div className="help-shot-missing" role="img" aria-label={caption}>
      <ImageOff size={18} aria-hidden="true" />
    </div>
  ) : (
    <img src={shotUrl(id)} alt={caption} loading="lazy" onError={() => setFailed(true)} />
  );
}

/**
 * Renders one docs page's Markdown (DOCS_CONTRACT dialect) as React. Headings
 * get the contract's stable ids (`data-heading-id`), which deep links and the
 * "On this page" scroll-spy look up.
 */
export default function DocMarkdown({ page, ctx }) {
  const { t } = useTranslation();
  const blocks = useMemo(() => assignHeadingIds(parseDocMarkdown(page?.markdown), page), [page]);

  const renderBlocks = (list, prefix) =>
    list.map((block, i) => {
      const key = `${prefix}-${i}`;
      switch (block.type) {
        case 'heading': {
          const Tag = `h${Math.min(6, Math.max(2, block.level))}`;
          const { id } = block;
          return (
            <Tag key={key} data-heading-id={id} id={`help-h-${id}`}>
              {renderInline(block.text, ctx, key)}
            </Tag>
          );
        }
        case 'paragraph':
          return <p key={key}>{renderInline(block.text, ctx, key)}</p>;
        case 'list': {
          const Tag = block.ordered ? 'ol' : 'ul';
          return (
            <Tag key={key} className={block.ordered ? 'help-steps' : undefined}>
              {block.items.map((item, j) => (
                <li key={j}>{renderInline(item, ctx, `${key}-${j}`)}</li>
              ))}
            </Tag>
          );
        }
        case 'code':
          return (
            <pre key={key} className="help-code">
              <code>{block.code}</code>
            </pre>
          );
        case 'quote':
          return <blockquote key={key}>{renderInline(block.text, ctx, key)}</blockquote>;
        case 'hr':
          return <hr key={key} />;
        case 'table':
          return (
            <div key={key} className="help-table-wrap panel-scroll">
              <table className="help-table">
                <thead>
                  <tr>
                    {block.head.map((h, j) => (
                      <th key={j}>{renderInline(h, ctx, `${key}-h${j}`)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {block.rows.map((row, r) => (
                    <tr key={r}>
                      {block.head.map((_, j) => (
                        <td key={j}>{renderInline(row[j] || '', ctx, `${key}-${r}-${j}`)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        case 'callout': {
          const Icon = CALLOUT_ICON[block.kind] || Info;
          return (
            <div key={key} className={`help-callout help-callout--${block.kind}`} role="note">
              <Icon size={17} aria-hidden="true" />
              <div className="help-callout-body">
                <span className="help-sr">{t(`help.callout.${block.kind}`)}</span>
                {renderBlocks(block.blocks, key)}
              </div>
            </div>
          );
        }
        case 'figure':
          return (
            <figure key={key} className={`help-figure help-figure--${block.media}`}>
              {block.media === 'video' ? (
                <div className="help-figure-video">
                  <DocVideo id={block.id} label={inlineText(block.caption)} />
                </div>
              ) : (
                <Shot id={block.id} caption={inlineText(block.caption)} />
              )}
              {block.caption && <figcaption>{renderInline(block.caption, ctx, key)}</figcaption>}
            </figure>
          );
        default:
          return null;
      }
    });

  return <div className="help-md">{renderBlocks(blocks, 'b')}</div>;
}
